import { useEffect, useMemo, useState, type ReactElement } from 'react';
import { MaterialIcon } from '@lapo/ui';
import { PageType, readwiseNoteIds, type Category } from '@lapo/core';
import { displayTitle, isDailyJournalId, type LocalNote } from './noteIndex.js';
import { NoteIcon } from './NoteIcon.js';

/**
 * The Library: browse the document store by topic, then by recency.
 *
 *  1. a search box that filters the list by title;
 *  2. category blocks — tags/topics rolled up by the server's categorize routine, each an icon +
 *     document count; clicking one scopes the list to that category;
 *  3. a recent-documents list (newest first), paged 10 at a time. Daily journals live in the day
 *     stack, not here, and Readwise book highlights are hidden from this general list — they have
 *     their own category.
 */
const PAGE_SIZE = 10;
function relativeDay(ts: number, now: number): string {
  if (!ts) return '';
  const days = Math.floor((now - ts) / 86_400_000);
  if (days <= 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days} days ago`;
  return new Date(ts).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

export function LibraryView({
  notes,
  categories,
  onOpen,
  onToggleStar,
  onCreate,
}: {
  notes: LocalNote[];
  categories: Category[];
  onOpen: (noteId: string) => void;
  onToggleStar: (noteId: string) => void;
  onCreate: () => void;
}): ReactElement {
  const now = Date.now();
  const [query, setQuery] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // Journals belong to the continuous day stack, not the document Library. Filter by id, not type:
  // the server registry types many journal days as `document`, so a `type !== Journal` check alone
  // would still let day entries through.
  const documents = useMemo(() => notes.filter((n) => !isDailyJournalId(n.noteId)), [notes]);
  const byId = useMemo(() => new Map(documents.map((n) => [n.noteId, n])), [documents]);

  // Readwise highlights are hidden from the general list — they surface only via their own category.
  const readwiseIds = useMemo(() => new Set(readwiseNoteIds(categories)), [categories]);
  const selected = useMemo(
    () => (selectedId ? categories.find((c) => c.id === selectedId) ?? null : null),
    [categories, selectedId],
  );

  const visible = useMemo(() => {
    let list: LocalNote[];
    if (selected) {
      // Preserve the category's newest-first order, keeping only docs this device knows about.
      list = selected.noteIds.map((id) => byId.get(id)).filter((n): n is LocalNote => !!n);
    } else {
      list = documents.filter((n) => !readwiseIds.has(n.noteId));
    }
    const q = query.trim().toLowerCase();
    if (q) list = list.filter((n) => displayTitle(n).toLowerCase().includes(q));
    return [...list].sort((a, b) => b.updatedAt - a.updatedAt);
  }, [selected, documents, byId, readwiseIds, query]);

  const listLabel = selected ? selected.name : query.trim() ? 'Results' : 'Recent';

  // Page the list 10 at a time. Any change to the filter/search collapses back to the first page.
  const [page, setPage] = useState(0);
  useEffect(() => setPage(0), [selectedId, query]);
  const pageCount = Math.max(1, Math.ceil(visible.length / PAGE_SIZE));
  const clampedPage = Math.min(page, pageCount - 1);
  const pageItems = visible.slice(clampedPage * PAGE_SIZE, clampedPage * PAGE_SIZE + PAGE_SIZE);

  return (
    <div className="lapo-library">
      <header className="lapo-library-head">
        <h1 className="lapo-library-title">Library</h1>
        <button type="button" className="lapo-library-new" onClick={onCreate}>
          <MaterialIcon name="add" size={18} />
          New document
        </button>
      </header>

      <div className="lapo-library-search">
        <MaterialIcon name="search" size={20} />
        <input
          type="text"
          className="lapo-library-search-input"
          placeholder="Search the library…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          aria-label="Search the library"
        />
        {query && (
          <button
            type="button"
            className="lapo-library-search-clear"
            onClick={() => setQuery('')}
            aria-label="Clear search"
          >
            <MaterialIcon name="close" size={18} />
          </button>
        )}
      </div>

      {categories.length > 0 && (
        <section className="lapo-library-cats" aria-label="Categories">
          {categories.map((c) => (
            <button
              key={c.id}
              type="button"
              className={`lapo-cat${c.id === selectedId ? ' lapo-active' : ''}`}
              aria-pressed={c.id === selectedId}
              onClick={() => setSelectedId((cur) => (cur === c.id ? null : c.id))}
            >
              <span className="lapo-cat-icon">
                <MaterialIcon name={c.icon} size={22} />
              </span>
              <span className="lapo-cat-text">
                <span className="lapo-cat-name">{c.name}</span>
                <span className="lapo-cat-count">
                  {c.count} {c.count === 1 ? 'document' : 'documents'}
                </span>
              </span>
            </button>
          ))}
        </section>
      )}

      <div className="lapo-library-listhead">
        <span className="lapo-library-listlabel">{listLabel}</span>
        {selected && (
          <button type="button" className="lapo-library-clear" onClick={() => setSelectedId(null)}>
            <MaterialIcon name="close" size={15} />
            Clear filter
          </button>
        )}
      </div>

      {visible.length ? (
        <>
        <ul className="lapo-library-list">
          {pageItems.map((n) => (
            <li key={n.noteId} className="lapo-library-row">
              <button type="button" className="lapo-library-open" onClick={() => onOpen(n.noteId)}>
                <span className="lapo-library-emoji"><NoteIcon icon={n.icon} type={n.type} /></span>
                <span className="lapo-library-name">{displayTitle(n)}</span>
                <span className="lapo-library-kind">{n.type === PageType.Journal ? 'Journal' : 'Document'}</span>
                <span className="lapo-library-date">{relativeDay(n.updatedAt, now)}</span>
              </button>
              <button
                type="button"
                className={`lapo-library-star${n.starred ? ' lapo-starred' : ''}`}
                title={n.starred ? 'Remove from favorites' : 'Add to favorites'}
                aria-label={n.starred ? 'Remove from favorites' : 'Add to favorites'}
                aria-pressed={n.starred}
                onClick={() => onToggleStar(n.noteId)}
              >
                <MaterialIcon name="star" size={18} />
              </button>
            </li>
          ))}
        </ul>
        {pageCount > 1 && (
          <nav className="lapo-library-pager" aria-label="Pagination">
            <button
              type="button"
              className="lapo-library-page"
              onClick={() => setPage(clampedPage - 1)}
              disabled={clampedPage === 0}
              aria-label="Previous page"
            >
              <MaterialIcon name="chevron_left" size={18} />
              Prev
            </button>
            <span className="lapo-library-pageinfo">
              Page {clampedPage + 1} of {pageCount}
            </span>
            <button
              type="button"
              className="lapo-library-page"
              onClick={() => setPage(clampedPage + 1)}
              disabled={clampedPage >= pageCount - 1}
              aria-label="Next page"
            >
              Next
              <MaterialIcon name="chevron_right" size={18} />
            </button>
          </nav>
        )}
        </>
      ) : (
        <div className="lapo-library-empty">
          {documents.length === 0 ? (
            <>
              <p>No pages yet.</p>
              <button type="button" className="lapo-library-new" onClick={onCreate}>
                <MaterialIcon name="add" size={18} />
                Create your first document
              </button>
            </>
          ) : (
            <p>No documents match.</p>
          )}
        </div>
      )}
    </div>
  );
}
